// Exibição de nome de lugar em duas linhas: a cidade em destaque na linha 1 e o
// nome COMPLETO do lugar na linha 2.
//
// O Nominatim devolve o `display_name` inteiro — "Cascavel, Metropolitano
// Cascavel/Catanduvas/Três Barras do Paraná, Paraná, Brasil". A linha 1 é o 1º
// segmento ("Cascavel"); a linha 2 é o nome inteiro, REPETINDO a cidade: quem lê
// precisa do contexto completo (região/UF/país) sem a cidade sair de vista.
//
// O segundo parâmetro `cityName` é o contexto municipal das paradas de carona
// ("Cascavel, PR", vindo do IBGE). Com ele, a linha 1 passa a ser a cidade e a
// linha 2 ganha o lugar + endereço, completando a cidade/UF que o nome do OSM
// não trouxe ("Havan, Avenida Tancredo Neves, Pioneiros Catarinenses, Cascavel,
// PR"). A detecção é pura (primeiro segmento do nome ≠ primeiro segmento da
// cidade) porque a flag `refined` do wizard NÃO é persistida no payload da carona.

export interface PlaceNameParts {
  /** Primeiro segmento, ex.: "Cascavel". `""` quando o nome é vazio. */
  primary: string
  /** Linha 2, ex.: "Cascavel, Paraná, Brasil" ou "Havan, Av. Brasil, Cascavel, PR". `null` quando não há. */
  secondary: string | null
}

/**
 * Junta os segmentos do nome aparados ("Cascavel,, PR," → "Cascavel, PR").
 * A linha 2 é o nome completo, mas sem vírgula solta nem espaço sobrando.
 */
function splitSegments(value: string): string[] {
  return value
    .split(",")
    .map((segment) => segment.trim())
    .filter(Boolean)
}

function joinSegments(value: string): string {
  return splitSegments(value).join(", ")
}

/**
 * Compara ignorando acento/caixa. Dado antigo pode ter o nome do município sem
 * acento ("Corbelia") e o `cityName` IBGE com ("Corbélia") — sem isso a parada
 * de cidade seria classificada como POI refinado e a linha 2 duplicaria a cidade.
 */
function normalizeForKey(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
}

export function splitPlaceName(
  name: string | null | undefined,
  cityName: string | null | undefined = null
): PlaceNameParts {
  const raw = (name ?? "").trim()
  const city = (cityName ?? "").trim()
  if (!raw && !city) return { primary: "", secondary: null }

  const rawFull = joinSegments(raw)
  const cityFull = joinSegments(city)
  const comma = raw.indexOf(",")
  const rawPrimary = comma === -1 ? rawFull : raw.slice(0, comma).trim()
  const cityComma = city.indexOf(",")
  const cityPrimary = cityComma === -1 ? cityFull : city.slice(0, cityComma).trim()
  // Sem contexto municipal: linha 1 = 1º segmento, linha 2 = nome completo.
  // Nome sem vírgula é uma linha só (nada a completar).
  if (!cityFull) {
    if (!rawFull) return { primary: "", secondary: null }
    if (comma === -1) return { primary: rawFull, secondary: null }
    // Nome que começa por vírgula (", PR"): não existe 1º segmento — a linha 1
    // fica com o resto e a linha 2 some, senão as duas linhas seriam iguais.
    if (!rawPrimary) return { primary: rawFull, secondary: null }
    return { primary: rawPrimary, secondary: rawFull === rawPrimary ? null : rawFull }
  }

  const primary = cityPrimary || rawPrimary || cityFull
  if (!primary) return { primary: "", secondary: null }

  // Sem nome ou nome igual à cidade: a linha 2 é a própria cidade.
  if (!rawFull) return { primary, secondary: cityFull === primary ? null : cityFull }

  const refined = normalizeForKey(rawPrimary) !== normalizeForKey(cityPrimary)
  if (refined) {
    // Parada refinada: nome do lugar + endereço + cidade/UF. O nome do OSM pode
    // parar na cidade ("…, Cascavel") enquanto o IBGE traz a UF ("Cascavel, PR"):
    // anexa só os segmentos que faltam, senão vira "Cascavel, Cascavel, PR".
    // A âncora é o último segmento do nome igual à cidade; sem ela, o lugar não
    // é daquele município e o contexto municipal entra inteiro.
    const rawSegments = splitSegments(rawFull)
    const citySegments = splitSegments(cityFull)
    const cityKey = normalizeForKey(citySegments[0])
    let anchor = -1
    for (let i = rawSegments.length - 1; i >= 0; i--) {
      if (normalizeForKey(rawSegments[i]) === cityKey) {
        anchor = i
        break
      }
    }
    if (anchor === -1) return { primary, secondary: `${rawFull}, ${cityFull}` }

    // Só o que já não está depois da cidade ("…, Cascavel, PR" não ganha "PR").
    const after = rawSegments.slice(anchor + 1).map(normalizeForKey)
    const missing = citySegments
      .slice(1)
      .filter((segment) => !after.includes(normalizeForKey(segment)))
    return {
      primary,
      secondary: missing.length > 0 ? `${rawFull}, ${missing.join(", ")}` : rawFull,
    }
  }

  // É a cidade: nome completo quando tem vírgula, senão o contexto municipal.
  if (comma !== -1) return { primary, secondary: rawFull === primary ? null : rawFull }
  return { primary, secondary: cityFull === primary ? null : cityFull }
}
